import { useEffect, useState } from 'react';
import { projectStatusReasonSchema } from '@forecastify/shared';
import { Label, Textarea } from '@forecastify/ui';
import { useT } from '../../i18n';
import { EditorDialog } from '../settings/editor-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · why a project's status is moving (the product owner).        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Every status change asks for a reason — from the header pill, from the archive item, from the
 * list's bulk archive. The reason is saved on the project (the pill's hover shows the latest) and
 * in the audit row of the change, which is where the project's history reads the rest.
 *
 * ⚠️ **Validated against the SHARED schema**, so the button cannot enable on something the server
 * would refuse — the alert dismissal's shape (`dismiss-alert-dialog.tsx`).
 *
 * ⚠️ **It stays open until the write lands.** A refusal shows in `error`, with the reason still
 * typed in; the caller closes it on success by setting `open` to false.
 */
export function StatusReasonDialog({
  open,
  description,
  busy = false,
  error = null,
  onClose,
  onConfirm,
}: {
  readonly open: boolean;
  /** Already translated: what is changing — *Aktív → Archivált*, or how many projects. */
  readonly description: string;
  readonly busy?: boolean;
  readonly error?: string | null;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string) => void;
}) {
  const t = useT();
  const [reason, setReason] = useState('');

  /* Cleared once the dialog closes — after the write, so a failed write keeps what was typed. */
  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const valid = projectStatusReasonSchema.safeParse(reason).success;

  return (
    <EditorDialog
      open={open}
      title={t('projects.statusReason.title')}
      description={description}
      saveLabel={t('projects.statusReason.save')}
      saveDisabled={!valid}
      busy={busy}
      error={error}
      onClose={onClose}
      onSave={() => {
        onConfirm(reason.trim());
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="project-status-reason">{t('projects.statusReason.label')}</Label>
        <Textarea
          id="project-status-reason"
          rows={3}
          maxLength={500}
          value={reason}
          placeholder={t('projects.statusReason.placeholder')}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
        {/* The requirement is said where it is enforced, not only by a disabled button. */}
        {valid ? null : (
          <p className="text-muted-foreground text-[11px]">{t('projects.statusReason.required')}</p>
        )}
      </div>
    </EditorDialog>
  );
}
